import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import { getRoomAvailability, selectHotel } from "./features/hotel/hotelSlice";
import moment from "moment";
import { getIsoString } from "./helpers/date";
import {
  clearBooking,
  getBookingReservationPrice,
  selectBooking,
  setBookingPeopleCount,
  setBookingPrices,
} from "./features/booking/bookingSlice";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { refreshReservations, syncRoomRatesWithVoucher } from "./helpers/roomCategory";
import Navbar from "./components/Navbar";
import CalendarTop from "./components/Calendar/CalendarTop";
import Cart from "./components/Cart";
import { LoaderOverlay } from "./components/LoaderOverlay";
import SelectHotel from "./scenes/SelectHotel";
import Calendar from "./scenes/Calendar";
import {
  GTMTrackingAddToCart,
  GTMTrackingBeginCheckout,
  GTMTrackingSelectAccommodation,
  GTMTrackingSelectPage,
} from "./helpers/GTMTracking";
import { findProduct, getPriceByRate } from "./helpers/reservation";
import Accommodation from "./scenes/Accommodation";
import Extras from "./scenes/Extras";
import Summary from "./scenes/Summary";
import Details from "./scenes/Details";
import Payment from "./scenes/Payment";
import RedirectPayment from "./scenes/RedirectPayment";
import Success from "./scenes/Success";
import RedirectHotel from "./scenes/RedirectHotel";
import PageNotFound from "./scenes/PageNotFound";
import ErrorButton from "./scenes/ErrorButton";
import { syncCultureWithI18n } from "./helpers/language";

function App() {
  const dispatch = useAppDispatch();
  const hotel = useAppSelector(selectHotel);
  const booking = useAppSelector(selectBooking);
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * On hotel load, prefer the browser language when the hotel supports it.
   * Do not also push i18n back to the previous Redux culture in this effect:
   * that ping-pongs setCulture ↔ changeLanguage and exceeds React's update depth
   * on city landing pages (/amsterdam, /rotterdam, …).
   */
  useEffect(() => {
    if (!hotel.HotelCode) return;

    syncCultureWithI18n(hotel.HotelCode);
  }, [hotel.HotelCode]);

  /**
   * Clear persisted booking when its arrival date is in the past
   */
  useEffect(() => {
    if (!["/accommodation", "/extras", "/summary", "/details"].includes(location.pathname)) return;
    if (booking.StartUtc !== null && booking.StartUtc < getIsoString(moment().utc().startOf("day"))) {
      dispatch(clearBooking());
      navigate("/calendar");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, booking.StartUtc]);

  /**
   * Refresh reservations when dependencies change
   */
  useEffect(() => {
    syncRoomRatesWithVoucher();
    refreshReservations();
  }, [
    hotel.RoomCategoryAvailabilities,
    booking.Rooms,
    booking.Products,
    booking.VoucherCode,
    booking.RateGroupId,
  ]);

  /**
   * Get room availability for discount when voucher code changes
   */
  useEffect(() => {
    if (booking.StartUtc && booking.EndUtc) {
      dispatch(
        getRoomAvailability({
          StartUtc: booking.StartUtc,
          EndUtc: booking.EndUtc,
          VoucherCode: booking.VoucherCode,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.VoucherCode]);

  /**
   * Set person count and prices when reservations, period or people change
   */
  useEffect(() => {
    if (
      booking.Reservations.length > 0 &&
      location.pathname !== "/" &&
      hotel.HotelCode
    ) {
      // Set people count
      const count = booking.Reservations.reduce((acc, curr) => {
        return (acc += curr.AdultCount);
      }, 0);

      const currencyCode = hotel.Culture.CurrencyCode;
      let maxPrice = 0;
      let price = 0;
      let discount = 0;

      // Combine the same reservations
      booking.Reservations.forEach((reservation) => {
        const pricing = getPriceByRate(reservation);
        if (pricing) {
          maxPrice += pricing.MaxPrice.Total[currencyCode];
          price += pricing.Price.Total[currencyCode];
          discount +=
            pricing.MaxPrice.Total[currencyCode] -
            pricing.Price.Total[currencyCode];
        }
      });

      // // Get extras
      booking.Products.forEach((p) => {
        const product = findProduct(p.Id);
        if (product) {
          const productPrice =
            product.Prices[currencyCode] *
            booking.PeopleCount *
            booking.NightCount;
          maxPrice += productPrice;
          price += productPrice;
        }
      });

      dispatch(
        setBookingPrices({
          currencyCode: currencyCode,
          maxPrice: maxPrice,
          price: price,
          discount: discount,
        }),
      );

      // Dispatch all
      dispatch(setBookingPeopleCount(count));
      dispatch(getBookingReservationPrice());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    booking.Reservations,
    booking.NightCount,
    booking.PeopleCount,
    hotel.Culture,
  ]);

  /**
   * Next step in booking process
   */
  const next = () => {
    // GTM DATALAYER
    GTMTrackingSelectPage(location.pathname);

    if (location.pathname === "/calendar") {
      // GTM DATALAYER
      GTMTrackingSelectAccommodation();
      navigate("/accommodation");
    } else if (location.pathname === "/accommodation") {
      if (booking.Rooms.length > 0) {
        navigate("/summary");
      } else if (hotel.Products.length === 0) {
        navigate("/summary");
      } else {
        navigate("/extras");
      }
    } else if (location.pathname === "/extras") {
      // GTM DATALAYER
      GTMTrackingAddToCart();
      navigate("/summary");
    } else if (location.pathname === "/summary") {
      // GTM DATALAYER
      GTMTrackingBeginCheckout();
      navigate("/details");
    }
  };

  return (
    <div
      className={`app flex flex-col text-fluid-base h-dvh max-w-2xl group/body ${window.self !== window.top ? "is-iframe" : ""}`}
    >
      {hotel.HotelCode &&
        [
          "/calendar",
          "/accommodation",
          "/extras",
          "/summary",
          "/details",
          "/payment",
          "/success",
        ].includes(location.pathname) && <Navbar />}

      {["/calendar"].includes(location.pathname) && <CalendarTop />}

      <div className="flex flex-grow relative">
        <Routes>
            <Route index element={<SelectHotel />} />
            <Route
              path="calendar"
              element={
                hotel.RoomCategories.length === 0 ? (
                  <Navigate replace to="/" />
                ) : (
                  <Calendar />
                )
              }
            />
            <Route
              path="accommodation"
              element={
                booking.StartUtc === null || booking.EndUtc === null ? (
                  <Navigate replace to="/calendar" />
                ) : (
                  <Accommodation />
                )
              }
            />
            <Route
              path="extras"
              element={
                booking.Rooms && booking.Rooms.length === 0 ? (
                  <Navigate replace to="/accommodation" />
                ) : (
                  <Extras />
                )
              }
            />
            <Route
              path="summary"
              element={
                booking.Rooms && booking.Rooms.length === 0 ? (
                  <Navigate replace to="/accommodation" />
                ) : (
                  <Summary />
                )
              }
            />
            <Route
              path="details"
              element={
                booking.Rooms && booking.Rooms.length === 0 ? (
                  <Navigate replace to="/accommodation" />
                ) : (
                  <Details />
                )
              }
            />
            <Route
              path="payment"
              element={
                booking.Rooms && booking.Rooms.length === 0 ? (
                  <Navigate replace to="/accommodation" />
                ) : (
                  <Payment />
                )
              }
            />
            <Route path="success" element={<Success />} />
            <Route path="redirect-payment" element={<RedirectPayment />} />
            <Route
              path="amsterdam"
              element={<RedirectHotel hotelCode="AMS" />}
            />
            <Route
              path="rotterdam"
              element={<RedirectHotel hotelCode="RTM" />}
            />
            <Route
              path="copenhagen"
              element={<RedirectHotel hotelCode="CPH" />}
            />
            <Route
              path="reykjavik"
              element={<RedirectHotel hotelCode="RKV" />}
            />
            <Route path="error-button" element={<ErrorButton />} />
            <Route path="*" element={<PageNotFound />} />
          </Routes>
      </div>

      {["/calendar"].includes(location.pathname) && <Cart next={next} />}

      <LoaderOverlay />
    </div>
  );
}

export default App;
